import { logWarn } from './logger';

/**
 * Native-App (Capacitor) Detection & Helper-Utilities.
 *
 * Läuft der Code in der nativen Capacitor-App, injiziert die Runtime das
 * window.Capacitor-Global. Bewusst KEIN Import aus @capacitor/core oder
 * anderen @capacitor/*-Paketen im src/-Code, damit das Web-Bundle frei von
 * Capacitor-Code bleibt (Konvention, ursprünglich in InstallAppBanner.js
 * eingeführt). Plugins werden ausschließlich über das nativ injizierte
 * window.Capacitor.Plugins.<Name> angesprochen — lässt sich in
 * Playwright-Tests einfach stubben (window.Capacitor vor dem Laden setzen).
 *
 * @module utils/nativeApp
 */

/** Custom-URL-Scheme der App (siehe android/app/src/main/res/values/strings.xml). */
export const NATIVE_OAUTH_REDIRECT_SCHEME = 'de.rentinger.app';

/** Authority/Host-Teil der OAuth-Rückkehr-Deep-Link-URL. */
export const NATIVE_OAUTH_REDIRECT_HOST = 'oauth-callback';

/**
 * True, wenn der Code aktuell in der nativen Capacitor-App läuft (Android/iOS).
 * @returns {boolean}
 */
export const isNativeApp = () => {
  const cap = window.Capacitor;
  return Boolean(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform());
};

/**
 * Liefert ein natives Capacitor-Plugin über das global injizierte
 * window.Capacitor.Plugins-Objekt, oder null, wenn nicht verfügbar
 * (Web-Build, Plugin nicht registriert, Runtime noch nicht bereit, ...).
 * @param {string} name - Plugin-Name, z.B. 'App' oder 'Browser'
 * @returns {object|null}
 */
export const getCapacitorPlugin = (name) => {
  const plugins = window.Capacitor && window.Capacitor.Plugins;
  return (plugins && plugins[name]) || null;
};

/**
 * Öffnet eine URL — in der nativen App über das Capacitor Browser-Plugin
 * (In-App-Browser-Tab / Custom Tab / SFSafariViewController), im Web per
 * klassischer Navigation. Wirft NIE: schlägt der native Aufruf fehl (rejected
 * Promise) oder ist das Plugin nicht verfügbar, wird auf window.location.href
 * zurückgefallen. `Browser.open` wird bewusst awaited (nicht fire-and-forget),
 * damit eine Rejection hier abgefangen werden kann statt als unhandled
 * promise rejection zu verpuffen.
 * @param {string} url
 * @returns {Promise<void>}
 */
export const openExternal = async (url) => {
  if (isNativeApp()) {
    const Browser = getCapacitorPlugin('Browser');
    if (Browser && typeof Browser.open === 'function') {
      try {
        await Browser.open({ url });
        return;
      } catch (e) {
        logWarn('[nativeApp] Browser.open fehlgeschlagen, Fallback auf window.location.href', e);
      }
    }
  }
  window.location.href = url;
};

/**
 * Schließt den nativen In-App-Browser-Tab, falls einer offen ist. Guarded und
 * schluckt Fehler bewusst — wird typischerweise "fire and forget" aufgerufen,
 * nachdem ein Deep-Link die App wieder in den Vordergrund geholt hat.
 */
export const closeInAppBrowser = () => {
  if (!isNativeApp()) return;
  const Browser = getCapacitorPlugin('Browser');
  if (Browser && typeof Browser.close === 'function') {
    try {
      Browser.close();
    } catch (e) {
      // Fehler bewusst ignoriert — der In-App-Browser ist an dieser Stelle
      // ohnehin meist schon (vom OS) geschlossen worden.
    }
  }
};
